// Enhanced Scoring Service with Partial Credit Integration
// Handles both legacy boolean scoring and new partial credit scoring

import { UserAnswer, EnhancedQuestion } from '../../types/questionFormats';

import { PartialCreditService, PartialCreditResult } from './partialCreditService';

export interface QuizScoreResult {
  total_score: number;
  max_possible_score: number;
  percentage: number;
  question_scores: QuestionScoreResult[];
  has_partial_credit: boolean;
  bonus_points: number;
  final_score: number;
}

export interface QuestionScoreResult {
  question_id: string | number;
  score: number;
  max_score: number;
  percentage: number;
  is_partial: boolean;
  format_type: string;
  explanation?: string;
  partial_credit_details?: PartialCreditResult;
}

export class EnhancedScoringService {
  private static normalizeComparisonValue(value: unknown): string {
    if (typeof value === 'number') {
      return String(value);
    }

    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  private static normalizeMCQText(value: unknown): string {
    return this.normalizeComparisonValue(value).replace(/[?!.,:;]+$/g, '');
  }

  private static getOrderingItemPosition(item: any, fallbackPosition: number): number {
    const rawPosition =
      typeof item?.position === 'number'
        ? item.position
        : typeof item?.correct_position === 'number'
          ? item.correct_position
          : null;

    return rawPosition === null ? fallbackPosition : rawPosition;
  }
  /**
   * Calculate comprehensive quiz score with partial credit support
   */
  static calculateQuizScore(
    questions: EnhancedQuestion[],
    userAnswers: (string | UserAnswer)[],
    timeBonus: boolean = false,
    timeSpentPerQuestion: number[] = []
  ): QuizScoreResult {
    const questionScores: QuestionScoreResult[] = [];
    let totalScore = 0;
    let maxPossibleScore = 0;
    let hasPartialCredit = false;
    let bonusPoints = 0;

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const userAnswer = userAnswers[i];
      const timeSpent = timeSpentPerQuestion[i] || 30;

      if (!userAnswer) {
        // No answer provided
        const questionResult: QuestionScoreResult = {
          question_id: question.id || i,
          score: 0,
          max_score: 1,
          percentage: 0,
          is_partial: false,
          format_type: question.question_type,
          explanation: 'No answer provided'
        };
        questionScores.push(questionResult);
        maxPossibleScore += 1;
        continue;
      }

      const questionResult = this.calculateQuestionScore(question, userAnswer, timeSpent);
      questionScores.push(questionResult);
      
      totalScore += questionResult.score;
      maxPossibleScore += questionResult.max_score;
      
      if (questionResult.is_partial) {
        hasPartialCredit = true;
      }

      // Time bonus for quick correct answers
      if (timeBonus && questionResult.score === questionResult.max_score) {
        if (timeSpent < 10) {
          bonusPoints += 0.2; // 20% bonus for very quick answers
        } else if (timeSpent < 20) {
          bonusPoints += 0.1; // 10% bonus for quick answers
        }
      }
    }

    const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;
    const finalScore = totalScore + bonusPoints;

    return {
      total_score: totalScore,
      max_possible_score: maxPossibleScore,
      percentage,
      question_scores: questionScores,
      has_partial_credit: hasPartialCredit,
      bonus_points: bonusPoints,
      final_score: finalScore
    };
  }

  /**
   * Calculate score for individual question with format-specific partial credit and gamification tracking
   */
  static calculateQuestionScore(
    question: EnhancedQuestion,
    userAnswer: string | UserAnswer,
    timeSpent: number = 30
  ): QuestionScoreResult {
    const questionId = question.id || 'unknown';
    const formatType = question.question_type;

    // Handle legacy string answers and new UserAnswer format
    if (typeof userAnswer === 'string') {
      return this.calculateLegacyQuestionScore(question, userAnswer);
    }

    // Enhanced multi-format scoring with partial credit
    switch (userAnswer.format) {
      case 'mcq_single':
        return this.calculateMCQSingleScore(question, userAnswer, questionId);
        
      case 'true_false':
        return this.calculateTrueFalseScoreWithGamification(question, userAnswer, questionId, timeSpent);
        
      case 'mcq_multiple':
        return this.calculateMCQMultipleScore(question, userAnswer, questionId);
        
      case 'fill_blank':
        return this.calculateFillBlankScore(question, userAnswer, questionId);
        
      case 'ordering':
        return this.calculateOrderingScore(question, userAnswer, questionId);
        
      default:
        return {
          question_id: questionId,
          score: 0,
          max_score: 1,
          percentage: 0,
          is_partial: false,
          format_type: formatType,
          explanation: `Unsupported answer format: ${(userAnswer as any).format}`
        };
    }
  }

  /**
   * Calculate score for legacy string-based answers (backward compatibility)
   */
  private static calculateLegacyQuestionScore(
    question: EnhancedQuestion,
    userAnswer: string
  ): QuestionScoreResult {
    const questionId = question.id || 'unknown';
    const normalizedUserAnswer = this.normalizeMCQText(userAnswer);
    const isCorrect =
      normalizedUserAnswer === this.normalizeMCQText(question.correct_answer) ||
      normalizedUserAnswer === this.normalizeMCQText(question.answer);
    
    return {
      question_id: questionId,
      score: isCorrect ? 1 : 0,
      max_score: 1,
      percentage: isCorrect ? 100 : 0,
      is_partial: false,
      format_type: 'legacy',
      explanation: isCorrect ? 'Correct answer' : 'Incorrect answer'
    };
  }

  private static calculateMCQSingleScore(
    question: EnhancedQuestion,
    userAnswer: any,
    questionId: string | number
  ): QuestionScoreResult {
    const formatData = question.format_data as any;
    const correctOption = formatData?.correct_option || 'a';
    const userSelection = userAnswer.answer;
    const options = Array.isArray(formatData?.options) ? formatData.options : [];
    const normalizedSelection = this.normalizeComparisonValue(userSelection);
    const normalizedSelectionText = this.normalizeMCQText(userSelection);
    const normalizedCorrectOption =
      typeof correctOption === 'string' ? correctOption.trim().toLowerCase() : correctOption;
    const correctAnswerText =
      typeof formatData?.correct_answer === 'string'
        ? formatData.correct_answer.trim()
        : typeof question.correct_answer === 'string'
          ? question.correct_answer.trim()
          : typeof question.answer === 'string'
            ? question.answer.trim()
            : '';

    let resolvedCorrectOptionText = correctAnswerText;
    if (!resolvedCorrectOptionText && normalizedCorrectOption && options.length > 0) {
      const optionIndex = normalizedCorrectOption.charCodeAt(0) - 97;
      if (optionIndex >= 0 && optionIndex < options.length) {
        resolvedCorrectOptionText = String(options[optionIndex] ?? '').trim();
      }
    }

    // For MCQ Single, support either stored option letters or stored option text.
    const isCorrect =
      normalizedSelection === normalizedCorrectOption ||
      (normalizedSelectionText.length > 0 &&
        resolvedCorrectOptionText.length > 0 &&
        normalizedSelectionText === this.normalizeMCQText(resolvedCorrectOptionText));
    
    return {
      question_id: questionId,
      score: isCorrect ? 1 : 0,
      max_score: 1,
      percentage: isCorrect ? 100 : 0,
      is_partial: false,
      format_type: 'mcq_single',
      explanation: isCorrect ? 'Correct selection' : 'Incorrect selection'
    };
  }

  private static calculateTrueFalseScoreWithGamification(
    question: EnhancedQuestion,
    userAnswer: any,
    questionId: string | number,
    timeSpent: number = 30
  ): QuestionScoreResult {
    const formatData = question.format_data as any;
    const correctAnswer = formatData?.correct_answer;
    const userSelection = userAnswer.answer;
    
    const isCorrect = userSelection === correctAnswer;
    let score = isCorrect ? 1 : 0;
    let explanation = isCorrect ? 'Correct answer' : 'Incorrect answer';
    
    // True/False gamification bonuses
    if (isCorrect) {
      // Quick decision bonus for True/False (binary choices should be intuitive)
      if (timeSpent < 5) {
        score += 0.2; // Lightning fast decision bonus
        explanation = 'Correct answer - Lightning decision! (+0.2 bonus)';
      } else if (timeSpent < 10) {
        score += 0.1; // Quick decision bonus
        explanation = 'Correct answer - Quick thinking! (+0.1 bonus)';
      }
    }
    
    return {
      question_id: questionId,
      score,
      max_score: 1,
      percentage: isCorrect ? 100 : 0,
      is_partial: score > 1, // Indicates bonus was awarded
      format_type: 'true_false',
      explanation
    };
  }
  
  // Keep the original method for backward compatibility
  private static calculateTrueFalseScore(
    question: EnhancedQuestion,
    userAnswer: any,
    questionId: string | number
  ): QuestionScoreResult {
    return this.calculateTrueFalseScoreWithGamification(question, userAnswer, questionId, 30);
  }

  private static calculateMCQMultipleScore(
    question: EnhancedQuestion,
    userAnswer: any,
    questionId: string | number
  ): QuestionScoreResult {
    const formatData = question.format_data as any;
    
    if (!formatData?.partial_credit) {
      // All-or-nothing scoring for MCQ Multiple without partial credit
      const correctIds = formatData.options.filter((opt: any) => opt.correct).map((opt: any) => opt.id);
      const selectedIds = userAnswer.answers;

      const normalizedCorrectIds = correctIds.map((id: unknown) => this.normalizeComparisonValue(id));
      const normalizedSelectedIds = selectedIds.map((id: unknown) => this.normalizeComparisonValue(id));

      const isExactMatch = normalizedCorrectIds.length === normalizedSelectedIds.length &&
        normalizedCorrectIds.every((id: string) => normalizedSelectedIds.includes(id));
      
      return {
        question_id: questionId,
        score: isExactMatch ? 1 : 0,
        max_score: 1,
        percentage: isExactMatch ? 100 : 0,
        is_partial: false,
        format_type: 'mcq_multiple',
        explanation: isExactMatch ? 'All correct options selected' : 'Incorrect selection'
      };
    }

    // Partial credit enabled
    const correctIds = formatData.options.filter((opt: any) => opt.correct).map((opt: any) => opt.id);
    const partialResult = PartialCreditService.calculateMCQMultipleScore(
      userAnswer.answers.map((id: unknown) => this.normalizeComparisonValue(id)),
      correctIds.map((id: unknown) => this.normalizeComparisonValue(id)),
      formatData.options.length,
      false
    );

    return {
      question_id: questionId,
      score: partialResult.total_score / partialResult.max_score,
      max_score: 1,
      percentage: partialResult.percentage,
      is_partial: partialResult.is_partial,
      format_type: 'mcq_multiple',
      explanation: partialResult.explanation,
      partial_credit_details: partialResult
    };
  }

  private static calculateFillBlankScore(
    question: EnhancedQuestion,
    userAnswer: any,
    questionId: string | number
  ): QuestionScoreResult {
    const formatData = question.format_data as any;
    
    if (!formatData?.partial_credit) {
      // All-or-nothing scoring for Fill Blank without partial credit
      const allCorrect = formatData.blanks.every((blank: any) => {
        const userInput = userAnswer.answers[blank.id] || '';
        const correctAnswer = formatData.case_sensitive ? blank.answer.trim() : blank.answer.trim().toLowerCase();
        const userProcessed = formatData.case_sensitive ? userInput.trim() : userInput.trim().toLowerCase();
        
        if (userProcessed === correctAnswer) return true;
        
        // Check alternates
        return blank.alternates?.some((alt: string) => {
          const altProcessed = formatData.case_sensitive ? alt.trim() : alt.trim().toLowerCase();
          return userProcessed === altProcessed;
        });
      });
      
      return {
        question_id: questionId,
        score: allCorrect ? 1 : 0,
        max_score: 1,
        percentage: allCorrect ? 100 : 0,
        is_partial: false,
        format_type: 'fill_blank',
        explanation: allCorrect ? 'All blanks correct' : 'Some blanks incorrect'
      };
    }

    // Partial credit enabled
    const correctAnswersMap = formatData.blanks.reduce((acc: any, blank: any) => {
      acc[blank.id] = {
        answer: blank.answer,
        alternates: blank.alternates,
        points: blank.points || 1
      };
      return acc;
    }, {});

    const partialResult = PartialCreditService.calculateFillBlankScore(
      userAnswer.answers,
      correctAnswersMap,
      formatData.case_sensitive,
      formatData.partial_credit_threshold || 70
    );

    return {
      question_id: questionId,
      score: partialResult.total_score / partialResult.max_score,
      max_score: 1,
      percentage: partialResult.percentage,
      is_partial: partialResult.is_partial,
      format_type: 'fill_blank',
      explanation: partialResult.explanation,
      partial_credit_details: partialResult
    };
  }

  private static calculateOrderingScore(
    question: EnhancedQuestion,
    userAnswer: any,
    questionId: string | number
  ): QuestionScoreResult {
    const formatData = question.format_data as any;
    
    if (!formatData?.partial_credit) {
      // All-or-nothing scoring
      const correctOrder = [...formatData.items]
        .sort((a: any, b: any) =>
          this.getOrderingItemPosition(a, Number.MAX_SAFE_INTEGER) -
          this.getOrderingItemPosition(b, Number.MAX_SAFE_INTEGER)
        )
        .map((item: any) => item.id);
      
      const isExactMatch = JSON.stringify(userAnswer.order) === JSON.stringify(correctOrder);
      
      return {
        question_id: questionId,
        score: isExactMatch ? 1 : 0,
        max_score: 1,
        percentage: isExactMatch ? 100 : 0,
        is_partial: false,
        format_type: 'ordering',
        explanation: isExactMatch ? 'Perfect order' : 'Incorrect order'
      };
    }

    // Partial credit enabled
    const correctOrder = [...formatData.items]
      .sort((a: any, b: any) =>
        this.getOrderingItemPosition(a, Number.MAX_SAFE_INTEGER) -
        this.getOrderingItemPosition(b, Number.MAX_SAFE_INTEGER)
      )
      .map((item: any) => item.id);
    
    const partialResult = PartialCreditService.calculateOrderingScore(
      userAnswer.order,
      correctOrder,
      formatData.strict_ordering,
      formatData.partial_credit
    );

    return {
      question_id: questionId,
      score: partialResult.total_score,
      max_score: 1,
      percentage: partialResult.percentage,
      is_partial: partialResult.is_partial,
      format_type: 'ordering',
      explanation: partialResult.explanation,
      partial_credit_details: partialResult
    };
  }

  /**
   * Calculate XP with partial credit bonus and True/False specific rewards
   */
  static calculateXPWithPartialCredit(
    scoreResult: QuizScoreResult,
    gameType: 'single' | 'multiplayer',
    gameContext?: any
  ): number {
    const baseXP = this.calculateBaseXP(scoreResult.percentage, gameType, gameContext);
    
    // Partial credit bonus - reward users for attempting complex questions
    let partialCreditBonus = 0;
    if (scoreResult.has_partial_credit) {
      const partialQuestions = scoreResult.question_scores.filter(q => q.is_partial);
      partialCreditBonus = partialQuestions.length * 2; // 2 XP bonus per partial credit question
    }
    
    // True/False format specific bonuses
    const trueFalseQuestions = scoreResult.question_scores.filter(q => q.format_type === 'true_false');
    let trueFalseBonus = 0;
    if (trueFalseQuestions.length > 0) {
      const correctTrueFalse = trueFalseQuestions.filter(q => q.percentage === 100).length;
      const trueFalseAccuracy = correctTrueFalse / trueFalseQuestions.length;
      
      // Perfect True/False streak bonus
      if (trueFalseAccuracy === 1.0 && trueFalseQuestions.length >= 3) {
        trueFalseBonus += 5; // Perfect True/False streak bonus
      }
      
      // Quick decision bonus for True/False (binary choice rewards quick thinking)
      if (gameType === 'single' && gameContext?.averageTimePerQuestion < 8) {
        trueFalseBonus += trueFalseQuestions.length * 1; // 1 XP per quick True/False answer
      }
    }
    
    // Multi-format variety bonus
    const formatVariety = new Set(scoreResult.question_scores.map(q => q.format_type)).size;
    const varietyBonus = formatVariety > 3 ? 5 : 0; // Bonus for diverse question formats
    
    return baseXP + partialCreditBonus + trueFalseBonus + varietyBonus + scoreResult.bonus_points;
  }

  private static calculateBaseXP(
    percentage: number,
    gameType: 'single' | 'multiplayer',
    gameContext?: any
  ): number {
    if (gameType === 'multiplayer') {
      let xpAmount = 15; // Base XP for participation
      
      // Performance bonuses
      const rank = gameContext?.rank || 1;
      const totalPlayers = gameContext?.totalPlayers || 2;
      
      if (rank === 1) xpAmount += 20; // Winner bonus
      else if (rank <= Math.ceil(totalPlayers / 2)) xpAmount += 10; // Top 50% bonus
      
      // Accuracy bonuses
      if (percentage >= 80) xpAmount += 10;
      if (percentage >= 90) xpAmount += 5;
      if (percentage === 100) xpAmount += 10;
      
      return xpAmount;
    } else {
      // Single player XP calculation
      let xpAmount = 10; // Base XP
      if (percentage >= 90) xpAmount += 15; // High score bonus
      if (percentage === 100) xpAmount += 10; // Perfect score bonus
      
      return xpAmount;
    }
  }

  /**
   * Format score for display with True/False specific highlights
   */
  static formatScoreForDisplay(scoreResult: QuizScoreResult): {
    primary: string;
    secondary: string;
    details: string[];
    achievements: string[];
  } {
    const { final_score, max_possible_score, percentage, has_partial_credit, bonus_points } = scoreResult;
    
    const primary = `${final_score.toFixed(1)}/${max_possible_score}`;
    const secondary = `${percentage.toFixed(1)}%`;
    
    const details: string[] = [];
    const achievements: string[] = [];
    
    if (has_partial_credit) {
      const partialQuestions = scoreResult.question_scores.filter(q => q.is_partial).length;
      details.push(`${partialQuestions} question${partialQuestions !== 1 ? 's' : ''} with partial credit`);
    }
    
    if (bonus_points > 0) {
      details.push(`+${bonus_points.toFixed(1)} bonus points`);
    }
    
    // True/False specific achievements
    const trueFalseQuestions = scoreResult.question_scores.filter(q => q.format_type === 'true_false');
    if (trueFalseQuestions.length > 0) {
      const correctTrueFalse = trueFalseQuestions.filter(q => q.percentage === 100).length;
      const trueFalseAccuracy = correctTrueFalse / trueFalseQuestions.length;
      
      if (trueFalseAccuracy === 1.0) {
        if (trueFalseQuestions.length >= 5) {
          achievements.push('🎯 Truth Seeker - Perfect True/False score!');
        } else if (trueFalseQuestions.length >= 3) {
          achievements.push('✅ Binary Master - All True/False correct!');
        }
      } else if (trueFalseAccuracy >= 0.8) {
        achievements.push('🔍 Good Discernment - Strong True/False performance!');
      }
      
      details.push(`${correctTrueFalse}/${trueFalseQuestions.length} True/False correct`);
    }
    
    return { primary, secondary, details, achievements };
  }
}
